import UIComponent from "sap/ui/core/UIComponent";
import Component from "sap/ui/core/Component";
import Logger from "sap/base/Log";
import {Router$BypassedEvent, Router$RouteMatchedEvent} from "sap/ui/core/routing/Router";
import JSONModel from "sap/ui/model/json/JSONModel";
import {Model$RequestFailedEvent} from "sap/ui/model/Model";

export default class RouteServices {
    protected component: UIComponent;

    public constructor(component: UIComponent) {
        this.component = component;

        this.getSessionModel()?.attachRequestFailed((event: Model$RequestFailedEvent) => {
            const params = event.getParameters();
            if (typeof params.statusCode === "number" && params.statusCode === 403) {
                this.clearSession();
            }
        }, this);

        this.getSettingsModel()?.attachRequestFailed((event: Model$RequestFailedEvent) => {
            const params = event.getParameters();
            if (typeof params.statusCode === "number" && [400, 403, 404, 405].includes(params.statusCode)) {
                this.clearSession();
                this.getOwnerComponent().getRouter().navTo("installer", {}, true);
            }
        });

        this.initRouter();
    }

    private getComponent(): UIComponent {
        return this.component
    }

    private getOwnerComponent(): UIComponent {
        return (Component.getOwnerComponentFor(this.getComponent()) as UIComponent) || this.getComponent();
    }

    /**
     * Attach routing event listeners.
     * router.initialize() is intentionally called here — the framework is not invoking
     * it automatically after IAsyncContentCreation content is ready.
     */
    public initRouter(): void {
        const router = this.getComponent().getRouter();
        if (router) {
            router.attachRouteMatched(this.onRouteMatched.bind(this), this);

            // Handle hash changes for nested component routing
            router.attachBypassed((event: Router$BypassedEvent) => {
                const hash = event.getParameter("hash");
                Logger.warning("### Works: route bypassed:", hash);
            });

            router.initialize();
        } else {
            Logger.error("### Works: Router is NULL! Check manifest routing configuration.");
        }
    }

    protected clearSession(): void {
        window.localStorage.removeItem("sessionId");
        window.localStorage.removeItem("userLanguage");
        this.getSessionModel()?.setData({});
    }

    /**
     * Get session model
     * Convenience method for child components
     */
    protected getSessionModel(): JSONModel {
        return this.getOwnerComponent().getModel("session") as JSONModel;
    }

    /**
     * Get settings model
     * Convenience method for child components
     */
    protected getSettingsModel(): JSONModel {
        return this.getOwnerComponent().getModel("settings") as JSONModel;
    }

    protected async onRouteMatched(event: Router$RouteMatchedEvent): Promise<void> {
        const routeName = event.getParameter("name");
        const settingsModel = this.getSettingsModel();
        const sessionsModel = this.getSessionModel();

        if (sessionsModel && settingsModel) {
            try {
                await Promise.all([
                    settingsModel.dataLoaded(),
                    sessionsModel.dataLoaded()
                ]);

                const settingsCode = settingsModel.getProperty("/code") as number;
                const sessionsCode = sessionsModel.getProperty("/code") as number;
                const sessionsError = sessionsModel.getProperty("/error") as string;
                const normalizedSessionsCode = Number(sessionsCode);

                // 1. settingsModel: 400, 405, 500 => view 500
                if ([400, 405, 500, 504].includes(settingsCode)) {
                    this.clearSession();
                    this.getOwnerComponent().getRouter().navTo("500", {}, true);
                    return;
                }
                // 2. settingsModel: 404 => installer
                if (settingsCode === 404) {
                    this.clearSession();
                    this.getOwnerComponent().getRouter().navTo("installer", {}, true);
                    return;
                }
                // 3. sessionsModel: error or code present => login
                if (sessionsError || (normalizedSessionsCode >= 400 && normalizedSessionsCode <= 500)) {
                    this.clearSession();
                    this.getOwnerComponent().getRouter().navTo("start", {}, true);
                    return;
                }

                // Default routing logic
                const isAuthenticated = this.isAuthenticated();
                const requiresAuth = !this.noAuthRoutes(routeName);
                if (requiresAuth && !isAuthenticated) {
                    this.clearSession();
                    this.getOwnerComponent().getRouter().navTo("start", {}, true);
                } else if (isAuthenticated && routeName === "start") {
                    this.getOwnerComponent().getRouter().navTo("system", {"target*": "launchpad"}, true);
                } else {
                    Logger.debug("### Works: Shell matched route. Handing off to Child Component.", routeName);
                }
            } catch (err) {
                Logger.error("### Works: Error loading models in onRouteMatched", err as Error);
                this.clearSession();
                this.getOwnerComponent().getRouter().navTo("500", {}, true);
            }
        }
    }

    /**
     * Check if route requires authentication
     * Can be overridden by child components to add package-specific routes
     */
    protected noAuthRoutes(routeName: string): boolean {
        const noAuthRequired = ["start", "pricing", "contact", "demo", "404", "500", "installer"];
        return noAuthRequired.includes(routeName);
    }

    /**
     * Check if user is authenticated by verifying session ID in local storage.
     * Shared authentication check across all components.
     */
    protected isAuthenticated(): boolean {
        const sessionId = window.localStorage.getItem("sessionId");
        return sessionId !== null && sessionId !== "undefined";
    }

    /**
     * Navigate to a specific target.
     * @public
     * @param routeHash Target name
     * @param [parameters] Navigation parameters
     * @param [replace] Defines if the hash should be replaced (no browser history entry) or set (browser history entry)
     */
    public async navTo(routeHash: string, parameters: object = {}, replace: boolean = true): Promise<void> {
        let component;

        if (this.getComponent().getRouter().getRoute(routeHash)) {
            // route matches, let's go
            this.getComponent().getRouter().navTo(routeHash, parameters, replace);
            return;
        }

        if (this.getOwnerComponent().getRouter().getRoute(routeHash)) {
            // parent component owns the route, let's go
            this.getOwnerComponent().getRouter().navTo(routeHash, parameters, replace);
            return;
        }

        if (routeHash.includes("/")) {
            const packageName = routeHash.split("/")[0];
            const componentId = `niteroi.packages.${packageName}`;
            component = Component.getComponentById(componentId) as UIComponent | undefined;
            if (!component) {
                const createPromise = this.getOwnerComponent().runAsOwner(() =>
                    Component.create({ id: componentId, name: componentId, manifest: true })
                ) as Promise<Component>;
                component = (await createPromise) as UIComponent;

                const router = component.getRouter();
                if (router.getRoute(routeHash)) {
                    router.navTo(routeHash, parameters, replace);
                } else {
                    void router.getHashChanger().setHash(routeHash);
                }
                return;
            }
        } else {
            // Root component for routes like Login, 404, 500.
            component = Component.getComponentById(`niteroi`) as UIComponent | undefined;
        }

        if (component) {
            const router = component.getRouter();
            if (router.getRoute(routeHash)) {
                router.navTo(routeHash, parameters, replace);
            } else {
                await router.getHashChanger().setHash(routeHash);
            }
        }
    }
}
